/**
 * <biosphere-predictive-search> — typeahead upgrade for the search form
 * rendered by snippets/biosphere-predictive-search.liquid. Behaviour:
 * reference/specs/predictive-search.md.
 *
 * Results are entirely server-rendered by the predictive-search section
 * and fetched through the platform's predictive search endpoint; this
 * file only debounces, aborts, swaps, and manages focus.
 */
const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

/**
 * The endpoint returns products only unless the request asks for more, so
 * the section's collection, page, article and suggestion branches never
 * rendered without this. Order is the endpoint's, not the section's — the
 * section decides what to show first.
 */
const RESOURCE_TYPES = "product,collection,page,article";
const RESOURCE_LIMIT = "8";
// Per type, not across all of them: the endpoint's default spreads one limit
// over every resource, so a term matching articles returns almost no products.
const RESOURCE_LIMIT_SCOPE = "each";

class BiospherePredictiveSearch extends HTMLElement {
  #input;
  #form;
  #panel;
  #live;
  #abort;
  #fetchController;
  #debounce;

  connectedCallback() {
    this.#input = this.querySelector(".biosphere-predictive-search__input");
    this.#panel = this.querySelector(".biosphere-predictive-search__results");
    this.#live = this.querySelector(".biosphere-predictive-search__live");
    if (!this.#input || !this.#panel) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#input.addEventListener("input", () => this.#onInput(), { signal });

    // A reset (the header panel's Clear) empties the field without firing
    // input, so the panel would otherwise keep showing the old query's
    // results. reset fires before the value clears, hence the deferral.
    this.#form = this.querySelector(".biosphere-predictive-search__form");
    this.#form?.addEventListener(
      "reset",
      () => setTimeout(() => this.#onInput()),
      { signal },
    );
    this.addEventListener("keydown", (event) => this.#onKeydown(event), {
      signal,
    });
    document.addEventListener(
      "click",
      (event) => {
        if (!this.contains(event.target)) this.#close();
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#fetchController?.abort();
    clearTimeout(this.#debounce);
  }

  #onInput() {
    clearTimeout(this.#debounce);
    const query = this.#input.value.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      this.#close();
      return;
    }
    this.#debounce = setTimeout(() => this.#search(query), DEBOUNCE_MS);
  }

  async #search(query) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const url = new URL(this.dataset.predictiveUrl, window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("section_id", "predictive-search");
    url.searchParams.set("resources[type]", RESOURCE_TYPES);
    url.searchParams.set("resources[limit]", RESOURCE_LIMIT);
    url.searchParams.set("resources[limit_scope]", RESOURCE_LIMIT_SCOPE);

    let text;
    try {
      const response = await fetch(url, {
        signal: this.#fetchController.signal,
      });
      text = await response.text();
    } catch (error) {
      if (error.name !== "AbortError") console.error(error);
      return;
    }

    const fetched = new DOMParser().parseFromString(text, "text/html");
    const fresh = fetched.querySelector("[data-predictive-content]");
    if (!fresh) return;

    this.#panel.replaceChildren(...fresh.childNodes);
    this.#panel.hidden = false;
    this.#input.setAttribute("aria-expanded", "true");

    if (this.#live) {
      const announcement = this.#panel.querySelector(
        "[data-predictive-announcement]",
      );
      this.#live.textContent = announcement?.textContent?.trim() ?? "";
    }
  }

  #onKeydown(event) {
    if (event.key === "Escape") {
      this.#close();
      this.#input.focus();
      return;
    }
    if (
      event.key === "ArrowDown" &&
      document.activeElement === this.#input &&
      !this.#panel.hidden
    ) {
      event.preventDefault();
      this.#panel.querySelector("a")?.focus();
    }
  }

  #close() {
    this.#panel.hidden = true;
    this.#input.setAttribute("aria-expanded", "false");
  }
}

if (!customElements.get("biosphere-predictive-search")) {
  customElements.define(
    "biosphere-predictive-search",
    BiospherePredictiveSearch,
  );
}
